import {
  CONFIDENCE_LEVELS,
  COUNTRIES,
  DATE_PRECISIONS,
  DIALOGUE_EVENT_TYPES,
  ENGAGEMENT_STATUSES,
  EVENT_TYPES,
  FINDINGS,
  INVOLVEMENT_TYPES,
  MAPPING_STATES,
  PRIORITIES,
  REDISTRIBUTION,
  RIGHTS_BASIS,
  SECTORS,
  SECURITY_IDENTIFIER_TYPES,
  SEVERITIES,
  SHARING,
  SOURCE_CLASSES,
  STAGES,
  THEMES,
} from '@fruuit/shared'
import type { FieldSpec } from './form'
import { vocabOptions } from './format'

/**
 * Field specs per kind (FORM-001..005). Labels follow the prototype's copy; required marks
 * and limits mirror the shared zod schemas (P-UI-008).
 */

const countryOptions = COUNTRIES.values.map((code) => ({
  value: code,
  label: `${(COUNTRIES.labels as Record<string, string>)[code] ?? code} (${code})`,
}))

export const ISSUER_FIELDS: readonly FieldSpec[] = [
  { name: 'legal_name', label: 'Legal name', type: 'text', required: true, full: true },
  { name: 'country', label: 'Country', type: 'select', options: countryOptions, required: true },
  {
    name: 'sector',
    label: 'Sector',
    type: 'select',
    options: vocabOptions(SECTORS),
    required: true,
  },
  {
    name: 'aliases',
    label: 'Other names',
    type: 'lines',
    hint: 'One per line: former names, brands or abbreviations used in sources.',
    full: true,
  },
  { name: 'website', label: 'Website', type: 'url' },
  { name: 'parent_issuer_id', label: 'Parent company', type: 'ref', refKind: 'issuers' },
  { name: 'description', label: 'Description', type: 'textarea' },
]

export const SECURITY_FIELDS: readonly FieldSpec[] = [
  {
    name: 'identifier_type',
    label: 'Identifier type',
    type: 'select',
    options: vocabOptions(SECURITY_IDENTIFIER_TYPES),
    required: true,
  },
  {
    name: 'identifier_value',
    label: 'Identifier',
    type: 'text',
    required: true,
    hint: 'ISINs are checked for format and check digit. Use Other for a non-ISIN identifier.',
  },
  { name: 'name', label: 'Security name', type: 'text' },
  {
    name: 'issuer_id',
    label: 'Issuer',
    type: 'ref',
    refKind: 'issuers',
    placeholder: 'None / unresolved',
  },
  {
    name: 'mapping_state',
    label: 'Mapping state',
    type: 'select',
    options: vocabOptions(MAPPING_STATES),
    required: true,
    hint: 'A verified mapping needs an issuer.',
  },
  { name: 'mapping_source', label: 'Mapping source', type: 'text', required: true },
  { name: 'effective_from', label: 'Effective from', type: 'date', required: true },
  { name: 'effective_to', label: 'Effective to', type: 'date' },
]

export const SOURCE_FIELDS: readonly FieldSpec[] = [
  { name: 'title', label: 'Title', type: 'text', required: true, full: true },
  { name: 'url', label: 'Source URL', type: 'url', required: true, full: true },
  {
    name: 'issuer_id',
    label: 'Company',
    type: 'ref',
    refKind: 'issuers',
    placeholder: 'Not company-specific',
  },
  { name: 'client_org_id', label: 'Client context', type: 'client' },
  {
    name: 'source_class',
    label: 'Source class',
    type: 'select',
    options: vocabOptions(SOURCE_CLASSES),
    required: true,
  },
  {
    name: 'sharing',
    label: 'Sharing',
    type: 'select',
    options: vocabOptions(SHARING),
    required: true,
    hint: 'Clients only see shareable sources cited by published work.',
  },
  { name: 'retrieved_at', label: 'Retrieved', type: 'date', required: true },
  {
    name: 'publication_date',
    label: 'Publication date',
    type: 'date',
    hint: 'Leave blank when unverified.',
  },
  {
    name: 'publication_date_precision',
    label: 'Publication date precision',
    type: 'select',
    options: vocabOptions(DATE_PRECISIONS),
  },
  { name: 'independence_group', label: 'Independence group', type: 'text' },
  { name: 'reporting_period_start', label: 'Reporting period start', type: 'date' },
  { name: 'reporting_period_end', label: 'Reporting period end', type: 'date' },
  {
    name: 'rights_basis',
    label: 'Rights basis',
    type: 'select',
    options: vocabOptions(RIGHTS_BASIS),
    required: true,
  },
  {
    name: 'redistribution',
    label: 'Redistribution',
    type: 'select',
    options: vocabOptions(REDISTRIBUTION),
    required: true,
  },
  { name: 'licence_ref', label: 'Licence reference', type: 'text' },
  {
    name: 'summary',
    label: 'Summary',
    type: 'textarea',
    required: true,
    hint: 'Summarise what the source says. Never paste full-text articles.',
  },
  { name: 'rights_terms', label: 'Rights terms', type: 'textarea', rows: 2 },
]

export const INCIDENT_FIELDS: readonly FieldSpec[] = [
  { name: 'title', label: 'Title', type: 'text', required: true, full: true },
  { name: 'issuer_id', label: 'Company', type: 'ref', refKind: 'issuers', required: true },
  { name: 'client_org_id', label: 'Client context', type: 'client' },
  { name: 'location', label: 'Location', type: 'text', required: true },
  { name: 'country', label: 'Country', type: 'select', options: countryOptions },
  { name: 'event_type', label: 'Event type', type: 'select', options: vocabOptions(EVENT_TYPES) },
  { name: 'theme', label: 'Theme', type: 'select', options: vocabOptions(THEMES) },
  { name: 'event_date', label: 'Event date', type: 'date', hint: 'Leave blank when unverified.' },
  {
    name: 'event_date_precision',
    label: 'Event date precision',
    type: 'select',
    options: vocabOptions(DATE_PRECISIONS),
  },
  {
    name: 'legal_status',
    label: 'Legal status',
    type: 'text',
    required: true,
    hint: 'For example: alleged, under investigation, charged, settled.',
  },
  { name: 'entity_role', label: 'Entity role', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea', required: true },
  { name: 'company_response', label: 'Company response', type: 'textarea', required: true },
  { name: 'uncertainty', label: 'Uncertainty and limits', type: 'textarea', required: true },
  {
    name: 'source_ids',
    label: 'Evidence sources',
    type: 'refs',
    refKind: 'sources',
    filter: (values) => (values.issuer_id ? { issuer_id: String(values.issuer_id) } : {}),
  },
]

/** Dated legal proceeding (VAL-024): all or none. */
export const INCIDENT_PROCEEDING_FIELDS: readonly FieldSpec[] = [
  { name: 'event_identity', label: 'Event identity', type: 'text' },
  { name: 'authority', label: 'Authority', type: 'text' },
  { name: 'proceeding_reference', label: 'Proceeding reference', type: 'text' },
  { name: 'legal_entity_id', label: 'Legal entity', type: 'ref', refKind: 'issuers' },
  { name: 'status_as_of', label: 'Status as of', type: 'date' },
  {
    name: 'previous_status_id',
    label: 'Previous status record',
    type: 'ref',
    refKind: 'incidents',
  },
]

export const ASSESSMENT_FIELDS: readonly FieldSpec[] = [
  { name: 'issuer_id', label: 'Company', type: 'ref', refKind: 'issuers', required: true },
  {
    name: 'client_org_id',
    label: 'Client context',
    type: 'client',
  },
  {
    name: 'finding',
    label: 'Conclusion',
    type: 'select',
    options: vocabOptions(FINDINGS),
    required: true,
    hint: 'A substantiated concern needs incident evidence. A no-concern release needs a complete search run.',
  },
  {
    name: 'priority',
    label: 'Priority',
    type: 'select',
    options: vocabOptions(PRIORITIES),
    required: true,
  },
  {
    name: 'norm',
    label: 'Norms',
    type: 'textarea',
    rows: 2,
    required: true,
    hint: 'The international norms engaged, e.g. UNGC principles 1–2 and the OECD Guidelines.',
  },
  {
    name: 'involvement',
    label: 'Involvement',
    type: 'select',
    options: vocabOptions(INVOLVEMENT_TYPES),
    required: true,
  },
  {
    name: 'severity',
    label: 'Severity',
    type: 'select',
    options: vocabOptions(SEVERITIES),
    required: true,
  },
  {
    name: 'confidence',
    label: 'Confidence',
    type: 'select',
    options: vocabOptions(CONFIDENCE_LEVELS),
    required: true,
  },
  { name: 'effective_date', label: 'Effective date', type: 'date' },
  { name: 'rationale', label: 'Rationale', type: 'textarea', rows: 4, required: true },
  { name: 'remediation', label: 'Remediation', type: 'textarea', rows: 3, required: true },
  {
    name: 'change_reason',
    label: 'Reason for this version',
    type: 'textarea',
    rows: 2,
    required: true,
  },
]

export const ENGAGEMENT_FIELDS: readonly FieldSpec[] = [
  { name: 'title', label: 'Case title', type: 'text', required: true, full: true },
  { name: 'issuer_id', label: 'Company', type: 'ref', refKind: 'issuers', required: true },
  { name: 'client_org_id', label: 'Client context', type: 'client' },
  {
    name: 'assessment_id',
    label: 'Published assessment',
    type: 'ref',
    refKind: 'assessments',
    required: true,
    hint: 'An engagement starts from a published assessment of the same company.',
    filter: (values) => ({
      state: 'published',
      ...(values.issuer_id ? { issuer_id: String(values.issuer_id) } : {}),
    }),
  },
  { name: 'theme', label: 'Theme', type: 'select', options: vocabOptions(THEMES) },
  {
    name: 'assigned_manager_id',
    label: 'Engagement manager',
    type: 'staff',
    role: 'manager',
    required: true,
  },
  {
    name: 'assigned_reviewer_id',
    label: 'Case reviewer',
    type: 'staff',
    role: 'reviewer',
    required: true,
    hint: 'Must differ from the manager. Only this reviewer releases milestones and correspondence.',
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    options: vocabOptions(ENGAGEMENT_STATUSES),
    required: true,
  },
  { name: 'due_date', label: 'Next action due', type: 'date', required: true },
  { name: 'next_action', label: 'Next action', type: 'text', required: true, full: true },
  { name: 'original_start', label: 'Original start', type: 'date' },
  { name: 'fruuit_start', label: 'Fruuit coverage since', type: 'date' },
  {
    name: 'closure_reason',
    label: 'Closure reason',
    type: 'textarea',
    rows: 2,
    hint: 'Required when the case is closed.',
  },
]

export const OBJECTIVE_FIELDS: readonly FieldSpec[] = [
  { name: 'title', label: 'Objective', type: 'text', required: true, full: true },
  { name: 'category', label: 'Category', type: 'select', options: vocabOptions(THEMES) },
  { name: 'owner_id', label: 'Owner', type: 'staff', required: true },
  { name: 'baseline', label: 'Baseline', type: 'textarea', required: true },
  { name: 'change_request', label: 'The change we seek', type: 'textarea', required: true },
  { name: 'next_action', label: 'Next action', type: 'text', required: true },
  { name: 'due_date', label: 'Due', type: 'date', required: true },
]

export const MILESTONE_FIELDS: readonly FieldSpec[] = [
  { name: 'stage', label: 'Stage', type: 'select', options: vocabOptions(STAGES), required: true },
  { name: 'assessment_date', label: 'Assessment date', type: 'date', required: true },
  {
    name: 'evidence_ids',
    label: 'Evidence',
    type: 'refs',
    refKind: 'sources',
    hint: 'Implementation (S3) and outcome (S4) stages require evidence about the case company.',
  },
  { name: 'rationale', label: 'Rationale', type: 'textarea', required: true },
  { name: 'limitations', label: 'Limitations', type: 'textarea', required: true },
]

export const DIALOGUE_FIELDS: readonly FieldSpec[] = [
  { name: 'title', label: 'Title', type: 'text', required: true, full: true },
  {
    name: 'event_type',
    label: 'Type',
    type: 'select',
    options: vocabOptions(DIALOGUE_EVENT_TYPES),
    required: true,
  },
  { name: 'event_date', label: 'Date', type: 'date', required: true },
  {
    name: 'substantive',
    label: 'Substantive response',
    type: 'checkbox',
    hint: 'The company engaged with the substance of the concern.',
    full: true,
  },
  { name: 'company_response', label: 'Company response', type: 'textarea', required: true },
  { name: 'fruuit_judgment', label: "Fruuit's judgment", type: 'textarea', required: true },
  { name: 'next_action', label: 'Next action', type: 'text', required: true },
  { name: 'due_date', label: 'Due', type: 'date', required: true },
  { name: 'source_ids', label: 'Evidence sources', type: 'refs', refKind: 'sources' },
]
